import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, Bot, TestTube, PlusCircle, Edit, DollarSign, BookOpen, FileText, Search, ScanText, Settings, Rocket, Plug } from 'lucide-react';
import { billingTokenService } from '../../services/api';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [credits, setCredits] = useState<string>('...');

  useEffect(() => {
    loadCredits();
    // Refresh credits every 60 seconds
    const interval = setInterval(loadCredits, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadCredits = async () => {
    try {
      const usage = await billingTokenService.getUsageSummary();
      if (usage.tokensRemaining !== null && usage.tokensRemaining !== undefined) {
        setCredits(usage.tokensRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 }));
      } else {
        setCredits('—');
      }
    } catch {
      setCredits('—');
    }
  };

  const navItems = [
    { path: '/', icon: MessageSquare, label: 'Chat Interface', section: 'Playground' },
    { path: '/agents', icon: Bot, label: 'Agents', section: 'Playground' },
    { path: '/explorer', icon: Search, label: 'Agent Explorer', section: 'Playground' },
    { path: '/ocr', icon: ScanText, label: 'OCR Widget', section: 'Playground' },
    { path: '/test', icon: TestTube, label: 'API Testing', section: 'Playground' },
    { path: '/create-agent', icon: PlusCircle, label: 'Create Agent', section: 'Management' },
    { path: '/edit-agent', icon: Edit, label: 'Edit Agent', section: 'Management' },
    { path: '/billing', icon: DollarSign, label: 'Billing', section: 'Management' },
    { path: '/settings', icon: Settings, label: 'Settings', section: 'Management' },
    { path: '/deployment', icon: Rocket, label: 'Deployment Studio', section: 'Deployment' },
    { path: '/clients', icon: DollarSign, label: 'Client Management', section: 'Deployment' },
    { path: '/plugins', icon: Plug, label: 'Plugins', section: 'Plugins' },
  ];

  const resourceItems = [
    { path: '/api-docs', icon: BookOpen, label: 'API Docs' },
    { path: '/curl-commands', icon: FileText, label: 'cURL Commands' },
  ];

  const groupedItems = navItems.reduce((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {} as Record<string, typeof navItems>);

  return (
    <div className="w-64 bg-[#0f172a] border-r border-slate-800 flex flex-col h-screen">
      {/* Logo */}
      <div className="p-6 border-b border-slate-800">
        <h1 className="text-lg font-bold text-white flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Vidhiguru Launchpad
        </h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {Object.entries(groupedItems).map(([section, items]) => (
          <div key={section} className="mb-6">
            <div className="px-6 mb-2">
              <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                {section}
              </h3>
            </div>
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-blue-600/10 text-blue-400 border-l-3 border-blue-500 font-semibold'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  }`}
                >
                  <Icon size={16} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}

        <div className="mb-6">
          <div className="px-6 mb-2">
            <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              Resources
            </h3>
          </div>
          {resourceItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-blue-600/10 text-blue-400 border-l-3 border-blue-500 font-semibold'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                <Icon size={16} />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center text-white text-sm font-semibold">
            S
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-200">Saurabh</div>
            <div className="text-[10px] text-slate-500">{credits} Credits</div>
          </div>
          <button
            onClick={() => { localStorage.removeItem('launchpad_auth'); window.location.reload(); }}
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-colors"
            title="Logout"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
